import { CSSProperties } from '@mui/material';
import { useState } from 'react';

import { type Veranstaltung, type VivaStatus, vivaStatus } from '../../dtos.ts';
import { freigabeWarningDialogContent } from './freigabeWarningDialogContent.ts';
import { useVeranstaltungenStatusMutation } from './hooks/useVeranstaltungenStatusMutation.ts';
import { PlanningWarningDialog } from './PlanningWarningDialog.tsx';
import { VivaStatusSwitch } from './VivaStatusSwitch.tsx';

type VivaStatusSwitchForVeranstaltungProps = {
  readonly veranstaltung: Pick<
    Veranstaltung,
    | 'akaVeranstaltungId'
    | 'sapStatus'
    | 'vivaStatus'
    | 'statusTransitions'
    | 'manuellerAusschreibungsprozess'
    | 'isKurzfristigerErsatztermin'
  >;
  readonly popperStyle?: CSSProperties;
};

export const VivaStatusSwitchForVeranstaltung = ({
  veranstaltung: {
    akaVeranstaltungId,
    sapStatus,
    vivaStatus: currentVivaStatus,
    statusTransitions,
    manuellerAusschreibungsprozess,
    isKurzfristigerErsatztermin,
  },
  popperStyle,
}: VivaStatusSwitchForVeranstaltungProps) => {
  const statusMutation = useVeranstaltungenStatusMutation();
  const [warningDialogOpen, setStatusWarningDialogOpen] = useState(false);

  const changeStatus = (newStatus: VivaStatus): void => {
    statusMutation.mutate({ newStatus, akaVeranstaltungIds: [akaVeranstaltungId] });
  };

  const handleStatusChange = (newStatus: VivaStatus): void => {
    if (newStatus === vivaStatus.FREIGEGEBEN && (manuellerAusschreibungsprozess || isKurzfristigerErsatztermin)) {
      setStatusWarningDialogOpen(true);
      return;
    }

    changeStatus(newStatus);
  };

  const abortFreigeben = () => {
    setStatusWarningDialogOpen(false);
  };

  const confirmFreigeben = () => {
    setStatusWarningDialogOpen(false);
    changeStatus(vivaStatus.FREIGEGEBEN);
  };

  return (
    <>
      <VivaStatusSwitch
        sapStatus={sapStatus}
        vivaStatus={currentVivaStatus}
        statusTransitions={statusTransitions}
        disabled={statusMutation.isPending}
        onChange={handleStatusChange}
        popperStyle={popperStyle}
      />

      <PlanningWarningDialog
        open={warningDialogOpen}
        warnings={[
          {
            id: freigabeWarningDialogContent.id,
            title: freigabeWarningDialogContent.title,
            message: manuellerAusschreibungsprozess
              ? freigabeWarningDialogContent.contentVAManuellerAusschreibungsprozess
              : freigabeWarningDialogContent.contentVAKurzfristigerErsatztermin,
          },
        ]}
        onApprove={confirmFreigeben}
        onCancel={abortFreigeben}
        loading={statusMutation.isPending}
        approveButtonLabel={freigabeWarningDialogContent.approveButtonLabel}
        questionText={freigabeWarningDialogContent.questionText}
      />
    </>
  );
};
